import { createTheme } from '@mui/material/styles';

// Professional Tech Teal Color Palette
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#0891b2', // Tech Teal (Cyan 600)
      light: '#06b6d4', // Light teal
      dark: '#0e7490', // Deep teal
      contrastText: '#ffffff', // White text on teal
    },
    secondary: {
      main: '#64748b', // Slate Gray
      light: '#94a3b8', // Light slate
      dark: '#475569', // Deep slate
      contrastText: '#ffffff', // White text
    },
    success: {
      main: '#10b981', // Professional Green
      light: '#34d399', // Light green
      dark: '#059669', // Deep green
    },
    background: {
      default: '#000000', // Matches the page backgrounds
      paper: '#1e293b', // Slate paper background
    },
    text: {
      primary: '#f8fafc', // Soft White
      secondary: '#cbd5e1', // Slate 300
    },
    divider: 'rgba(100, 116, 139, 0.2)', // Subtle slate divider
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontWeight: 700,
      fontSize: '3.5rem',
      color: '#f8fafc',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontWeight: 700,
      fontSize: '2.5rem',
      color: '#f8fafc',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontWeight: 600,
      fontSize: '1.75rem',
      color: '#f8fafc',
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontWeight: 600,
      fontSize: '1.4rem',
      color: '#f8fafc',
      letterSpacing: '0em',
    },
    h5: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontWeight: 600,
      fontSize: '1.2rem',
      color: '#0891b2',
    },
    h6: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontWeight: 500,
      fontSize: '1rem',
      color: '#64748b',
    },
    body1: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: '1rem',
      lineHeight: 1.7,
      color: '#cbd5e1',
    },
    body2: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#cbd5e1',
    },
    button: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0em',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          padding: '10px 24px',
          fontWeight: 600,
          transition: 'all 0.2s ease',
        },
        contained: {
          backgroundColor: '#0891b2',
          color: '#ffffff',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          '&:hover': {
            backgroundColor: '#0e7490',
            boxShadow: '0 4px 8px rgba(8, 145, 178, 0.3)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: '#0891b2',
          borderWidth: '1.5px',
          color: '#0891b2',
          '&:hover': {
            borderColor: '#0e7490',
            backgroundColor: 'rgba(8, 145, 178, 0.08)',
            borderWidth: '1.5px',
          },
        },
        text: {
          color: '#0891b2',
          '&:hover': {
            backgroundColor: 'rgba(8, 145, 178, 0.08)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(100, 116, 139, 0.2)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(30, 41, 59, 0.9)',
          border: '1px solid rgba(100, 116, 139, 0.2)',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'rgba(8, 145, 178, 0.4)',
            boxShadow: '0 4px 12px rgba(8, 145, 178, 0.2)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '6px',
          marginTop: 8,
          backgroundColor: 'rgba(30, 41, 59, 0.98)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(100, 116, 139, 0.3)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#000000',
          color: '#f8fafc',
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(8, 145, 178, 0.03) 0%, transparent 60%), radial-gradient(circle at 80% 80%, rgba(100, 116, 139, 0.02) 0%, transparent 60%)',
          backgroundAttachment: 'fixed',
        },
        img: {
          maxWidth: '100%',
          height: 'auto',
        },
        'img[loading="lazy"]': {
          opacity: 0,
          transition: 'opacity 0.3s',
        },
        'img[loading="lazy"].loaded': {
          opacity: 1,
        },
      },
    },
  },
});

export default theme; 